import { type RefObject, useLayoutEffect, useState } from 'react';

const SCROLL_MARGIN = 32;

type UseSuggestionCardPositioningProps = {
	cardRef: RefObject<HTMLDivElement>;
	id?: string;
	root?: Element;
	target?: HTMLSpanElement;
	targetEnd?: HTMLSpanElement;
};

type UseSuggestionCardPositioningResult = {
	anchorLeft: number | null;
	isCardBottomBelowViewport: boolean;
	isTargetAboveViewport: boolean;
	isTargetBelowViewport: boolean;
	isTargetEndAboveViewport: boolean;
	isTargetEndBelowViewport: boolean;
	isTargetRightOfViewport: boolean;
};

/**
 * Tracks the position of the suggestion card target relative to the editor viewport
 * using IntersectionObserver and ResizeObserver. Returns booleans describing where
 * the target/card sit relative to the viewport, plus the anchor's left offset used
 * to work around a Chromium bug with `anchor()` on `position: fixed` elements.
 */
export const useSuggestionCardPositioning = ({
	id,
	root,
	target,
	targetEnd,
	cardRef,
}: UseSuggestionCardPositioningProps): UseSuggestionCardPositioningResult => {
	const [anchorLeft, setAnchorLeft] = useState<number | null>(null);
	const [isTargetAboveViewport, setIsTargetAboveViewport] = useState(false);
	const [isTargetBelowViewport, setIsTargetBelowViewport] = useState(false);
	const [isCardBottomBelowViewport, setIsCardBottomBelowViewport] = useState(false);
	const [isTargetRightOfViewport, setIsTargetRightOfViewport] = useState(false);
	const [isTargetEndAboveViewport, setIsTargetEndAboveViewport] = useState(false);
	const [isTargetEndBelowViewport, setIsTargetEndBelowViewport] = useState(false);

	const cardHeight = cardRef.current?.offsetHeight ?? 0;
	const cardWidth = cardRef.current?.offsetWidth ?? 0;

	/**
	 * Workaround for a Chromium bug where using anchor() on a
	 * `position: fixed` element produces correct paint but incorrect hit-testing.
	 * https://issues.chromium.org/issues/512171942
	 *
	 * Only needed for when (!isTargetRightOfViewport &&
	 * (isTargetAboveViewport || isCardBottomBelowViewport || isTargetBelowViewport))
	 * as these combinations have position: fixed with non-static inline position.
	 */

	const shouldTrackAnchor =
		!isTargetRightOfViewport &&
		(isTargetAboveViewport || isCardBottomBelowViewport || isTargetBelowViewport);
	useLayoutEffect(() => {
		if (!target || !shouldTrackAnchor) {
			setAnchorLeft(null);
			return;
		}

		setAnchorLeft(target.getBoundingClientRect().left - (root?.getBoundingClientRect().left || 0));

		const observer = new ResizeObserver(() => {
			setAnchorLeft(
				target.getBoundingClientRect().left - (root?.getBoundingClientRect().left || 0),
			);
		});
		if (root) {
			observer.observe(root);
		}
		return () => {
			observer.disconnect();
		};
	}, [shouldTrackAnchor, target, root]);

	useLayoutEffect(() => {
		if (!id || !root || !target || !targetEnd || !cardRef.current) {
			setIsTargetRightOfViewport(false);
			setIsTargetEndAboveViewport(false);
			setIsTargetEndBelowViewport(false);
			setIsTargetBelowViewport(false);
			setIsTargetAboveViewport(false);
			setIsCardBottomBelowViewport(false);
			return;
		}

		const verticalCardTopObserver = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (!entry || !entry.rootBounds) {
					return;
				}
				const { rootBounds, boundingClientRect } = entry;
				setIsTargetAboveViewport(boundingClientRect.top < rootBounds.top);
				setIsCardBottomBelowViewport(boundingClientRect.bottom > rootBounds.bottom);
			},
			{
				root,
				rootMargin: `-${SCROLL_MARGIN}px 0px -${cardHeight + SCROLL_MARGIN}px 0px`,
			},
		);

		const verticalTargetObserver = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (!entry || !entry.rootBounds) {
					return;
				}
				const { rootBounds, boundingClientRect } = entry;

				setIsTargetBelowViewport(boundingClientRect.top > rootBounds.bottom);
			},
			{
				root,
				rootMargin: `${cardHeight + SCROLL_MARGIN}px 0px -${SCROLL_MARGIN}px 0px`,
			},
		);

		const targetEndObserver = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (!entry || !entry.rootBounds) {
					return;
				}
				const { rootBounds, boundingClientRect } = entry;
				setIsTargetEndAboveViewport(boundingClientRect.top < rootBounds.top);
				setIsTargetEndBelowViewport(boundingClientRect.bottom > rootBounds.bottom);
			},
			{
				root,
				rootMargin: `0px 0px -${cardHeight + SCROLL_MARGIN}px 0px`,
			},
		);

		const horizontalObserver = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (!entry || !entry.rootBounds) {
					return;
				}
				const { rootBounds, boundingClientRect } = entry;
				setIsTargetRightOfViewport(boundingClientRect.right > rootBounds.right);
			},
			{
				root,
				rootMargin: `0px -${cardWidth + SCROLL_MARGIN}px 0px 0px`,
			},
		);

		verticalCardTopObserver.observe(target);
		horizontalObserver.observe(target);
		targetEndObserver.observe(targetEnd);
		verticalTargetObserver.observe(target);
		return () => {
			verticalCardTopObserver.disconnect();
			horizontalObserver.disconnect();
			targetEndObserver.disconnect();
			verticalTargetObserver.disconnect();
		};
	}, [target, cardRef, root, targetEnd, cardHeight, cardWidth, id]);

	return {
		anchorLeft,
		isTargetAboveViewport,
		isTargetBelowViewport,
		isCardBottomBelowViewport,
		isTargetRightOfViewport,
		isTargetEndAboveViewport,
		isTargetEndBelowViewport,
	};
};
